var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { SEARCH_PAGE_INIT, } from '@lean-body/src/domain';
import { routes } from '@lean-body/src/route';
import { SearchResults, SelectOption, selectOptions } from '@lean-body/components/organisms';
import { SearchSortDialog } from '@lean-body/components/molecules';
import { Colors, MediaBreakPointUp, Sizes } from '@lean-body/components/styles';
import { ZIndex } from '../constants';
import { useQuery } from '@lean-body/src/util';
import { LocalStorage, SEARCH_HISTORIES_MAX_NUM } from '@lean-body/src/infra';
import { FIXED_FOOTER_HEIGHT } from '../organisms/fixed_footer';
import { SEARCH_SELECTED_TAB, SearchTabKeys } from './main_search';
import { SearchResultHeader } from '../organisms/search/search_result_header';
const { useState, useEffect, useLayoutEffect, useRef } = React;
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const parseIdList = (value) => (value || '')
    .split(',')
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);
// 名前に半角スペースが含まれていても、クエリ中のひとまとまりとして部位・インストラクターに割り当てる
const matchQueryNames = (query, tags, trainers) => {
    const candidates = [
        ...tags.map((tag) => ({ type: 'tag', id: tag.id, name: tag.name })),
        ...trainers.map((trainer) => ({ type: 'trainer', id: trainer.id, name: trainer.name })),
    ]
        .filter((item) => item.name)
        .sort((a, b) => b.name.length - a.name.length);
    let rest = query;
    const tagIDs = [];
    const trainerIDs = [];
    candidates.forEach((item) => {
        const pattern = new RegExp(`(^|\\s)${escapeRegExp(item.name)}(?=\\s|$)`);
        if (!pattern.test(rest))
            return;
        if (item.type === 'tag')
            tagIDs.push(item.id);
        else
            trainerIDs.push(item.id);
        rest = rest.replace(pattern, ' ');
    });
    return {
        tagIDs,
        trainerIDs,
        keyword: rest.trim().split(/\s+/).filter(Boolean).join(' '),
    };
};
export var PageStatus;
(function (PageStatus) {
    PageStatus["SearchingStart"] = "searching_start";
    PageStatus["InitialPageLoaded"] = "initial_page_loaded";
})(PageStatus || (PageStatus = {}));
export const SearchResultSectionTabs = {
    lessons: 'レッスン',
    seriesPrograms: 'シリーズ',
    playlists: 'プレイリスト',
};
const resultSectionForCategory = (category) => {
    if (category === 'series')
        return SearchResultSectionTabs.seriesPrograms;
    if (category === 'playlist')
        return SearchResultSectionTabs.playlists;
    return SearchResultSectionTabs.lessons;
};
export const SearchResultPage = ({ apiClient, tracker, config }) => {
    var _a, _b, _c;
    const query = useQuery();
    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigate();
    const localStorage = new LocalStorage();
    const currentSort = query.get('sort_by') || SelectOption.Favorite;
    const currentStyles = ((_a = query.get('style_id')) === null || _a === void 0 ? void 0 : _a.split(',').map(Number)) || [];
    const currentDifficulties = ((_b = query.get('difficulty_id')) === null || _b === void 0 ? void 0 : _b.split(',').map(Number)) || [];
    const currentDurations = ((_c = query.get('duration_id')) === null || _c === void 0 ? void 0 : _c.split(',').map(Number)) || [];
    const categoryQuery = query.get('category');
    const currentCategory = categoryQuery === 'series' || categoryQuery === 'playlist' ? categoryQuery : 'lesson';
    const currentTab = currentCategory === 'lesson'
        ? SearchResultSectionTabs[query.get(SEARCH_SELECTED_TAB)] || SearchResultSectionTabs.lessons
        : resultSectionForCategory(currentCategory);
    let searchHistories = localStorage.loadSearchHistories || [];
    const [lessonStyles, setLessonStyles] = useState();
    const [durations, setDurations] = useState();
    const [difficulties, setDifficulties] = useState();
    const [styleIDs, setStyleIDs] = useState(currentStyles);
    const [difficultyIDs, setDifficultyIDs] = useState(currentDifficulties);
    const [durationIDs, setDurationIDs] = useState(currentDurations);
    const urlQuery = (query.get('q') || '').trim();
    const urlTagIDs = parseIdList(query.get('tag_id'));
    const urlTrainerIDs = parseIdList(query.get('trainer_id'));
    // 部位・インストラクターを選んで遷移したときは、名前を入力欄に出さず絞り込みから始める
    const openedAsFilter = urlTagIDs.length > 0 || urlTrainerIDs.length > 0;
    const [tagIDs, setTagIDs] = useState(urlTagIDs);
    const [trainerIDs, setTrainerIDs] = useState(urlTrainerIDs);
    const [contentCategory, setContentCategory] = useState(currentCategory);
    const [partTags, setPartTags] = useState([]);
    const [trainers, setTrainers] = useState([]);
    const [keyword, setKeyword] = useState(openedAsFilter ? '' : query.get('q') || '');
    const [lessonsNextButtonDisplayable, setLessonsNextButtonDisplayable] = useState();
    const [playlistsNextButtonDisplayable, setPlaylistsNextButtonDisplayable] = useState();
    const [seriesProgramsNextButtonDisplayable, setSeriesProgramsNextButtonDisplayable] = useState();
    const [lessons, setLessons] = useState();
    const [lessonsCount, setLessonsCount] = useState();
    const [playlists, setPlaylists] = useState();
    const [playlistsCount, setPlaylistsCount] = useState();
    const [seriesPrograms, setSeriesPrograms] = useState();
    const [seriesProgramsCount, setSeriesProgramsCount] = useState();
    const [showSortDialog, setShowSortDialog] = useState(false);
    const [sortBy, setSortBy] = useState(currentSort);
    const [pageStatus, setPageStatus] = useState(PageStatus.SearchingStart);
    const [isLoadingNext, setIsLoadingNext] = useState(true);
    const [tab, setTab] = React.useState(currentTab);
    const [page, setPage] = useState(SEARCH_PAGE_INIT);
    const [historySearchTick, setHistorySearchTick] = useState(0);
    const searchRequestIdRef = useRef(0);
    const initialLocationKeyRef = useRef(location.key);
    // 初回表示のPOPは復元しない。別条件へ進んだあと、その初回へ戻ったときは復元する
    const hasLeftInitialEntryRef = useRef(false);
    // 名前一覧が届くまでは、選択した部位・インストラクター名をこの文字列のまま検索する
    const unresolvedQueryRef = useRef(openedAsFilter ? urlQuery : '');
    // 部位・インストラクター一覧が返る前にキーワードを変えたら、URLの名前を絞り込みへ割り当てない
    const keywordEditedRef = useRef(false);
    const displaySortLabel = selectOptions.find((v) => {
        return v.value === currentSort;
    }).name;
    const saveSearchHistory = (str) => {
        const tokens = str.trim().split(/\s+/).filter(Boolean);
        if (!tokens.length)
            return;
        tokens.forEach((token) => {
            searchHistories = searchHistories.filter((history) => history !== token);
            searchHistories.unshift(token);
        });
        searchHistories = searchHistories.slice(0, SEARCH_HISTORIES_MAX_NUM);
        localStorage.saveSearchHistories(searchHistories);
    };
    useEffect(() => {
        Promise.all([
            apiClient.fetchStyles(),
            apiClient.fetchDurationsForSearch(),
            apiClient.fetchDifficulties(),
            search(),
        ]).then(([styles, durations, difficulties]) => {
            setLessonStyles(styles);
            setDurations(durations);
            setDifficulties(difficulties);
        });
        Promise.all([apiClient.fetchPartTags(), apiClient.fetchTrainers()]).then(([tags, trainerList]) => {
            setPartTags(tags);
            setTrainers(trainerList);
            if (keywordEditedRef.current)
                return;
            const originalQuery = (query.get('q') || '').trim();
            const matched = matchQueryNames(originalQuery, tags, trainerList);
            setTagIDs((current) => (current.length ? current : matched.tagIDs));
            setTrainerIDs((current) => (current.length ? current : matched.trainerIDs));
            if (matched.tagIDs.length || matched.trainerIDs.length) {
                setKeyword((current) => (current === originalQuery ? matched.keyword : current));
            }
        });
        return () => {
            searchRequestIdRef.current += 1;
        };
    }, []);
    const buildSearchParams = (q = '', applied, keywordOverride) => {
        var _a, _b, _c, _d, _e;
        const params = { sortBy: sortBy };
        const keywordValue = keywordOverride !== undefined ? keywordOverride : q !== '' ? q : keyword;
        const styles = (_a = applied === null || applied === void 0 ? void 0 : applied.styleIDs) !== null && _a !== void 0 ? _a : styleIDs;
        const difficulties = (_b = applied === null || applied === void 0 ? void 0 : applied.difficultyIDs) !== null && _b !== void 0 ? _b : difficultyIDs;
        const durations = (_c = applied === null || applied === void 0 ? void 0 : applied.durationIDs) !== null && _c !== void 0 ? _c : durationIDs;
        const tags = (_d = applied === null || applied === void 0 ? void 0 : applied.tagIDs) !== null && _d !== void 0 ? _d : tagIDs;
        const selectedTrainers = (_e = applied === null || applied === void 0 ? void 0 : applied.trainerIDs) !== null && _e !== void 0 ? _e : trainerIDs;
        const selectedCategory = applied ? applied.category : contentCategory;
        const composedQuery = composeSearchText(keywordValue, tags, selectedTrainers);
        const namesPending = tags.some((id) => !partTags.some((tag) => tag.id === id)) ||
            selectedTrainers.some((id) => !trainers.some((trainer) => trainer.id === id));
        const pendingQuery = namesPending ? unresolvedQueryRef.current.trim() : '';
        const queryText = composedQuery && pendingQuery && !containsSearchPhrase(composedQuery, pendingQuery)
            ? `${composedQuery} ${pendingQuery}`
            : composedQuery || pendingQuery;
        if (queryText)
            params.q = queryText;
        params.category = selectedCategory || 'lesson';
        if (styles.length)
            params.styleId = styles.join(',');
        if (difficulties.length)
            params.difficultyId = difficulties.join(',');
        if (durations.length)
            params.durationId = durations.join(',');
        return params;
    };
    const search = (...args_1) => __awaiter(void 0, [...args_1], void 0, function* (nextPage = false, q = '', applied, keywordOverride) {
        const requestId = ++searchRequestIdRef.current;
        setNextButtonDisplayable(false);
        let pageCount = page;
        if (nextPage) {
            pageCount++;
            setPage(pageCount);
            setIsLoadingNext(true);
        }
        else {
            pageCount = SEARCH_PAGE_INIT;
            setPage(pageCount);
            // 再検索中は未取得・前回の一覧と件数を出さない
            setPageStatus(PageStatus.SearchingStart);
            setLessonsCount(undefined);
            setSeriesProgramsCount(undefined);
            setPlaylistsCount(undefined);
        }
        const searchParams = buildSearchParams(q, applied, keywordOverride);
        const searchResult = yield apiClient.search(searchParams, pageCount);
        // 新しいキーワード・絞り込みの検索が始まっていたら、先に飛んだ通信の結果は捨てる
        if (requestId !== searchRequestIdRef.current)
            return;
        setLessonsAndPlaylistsAndPrograms(nextPage, searchResult);
        if (keywordOverride !== undefined)
            saveSearchHistory(keywordOverride);
        changeNextButtonDisplayable(pageCount, searchResult);
        if (q !== '') {
            const selectedCategory = applied ? applied.category : contentCategory;
            setTab(resultSectionForCategory(selectedCategory));
            setPage(SEARCH_PAGE_INIT);
        }
        setIsLoadingNext(false);
        if (!nextPage)
            window.scrollTo(0, 0);
        setPageStatus(PageStatus.InitialPageLoaded);
    });
    // ブラウザの戻る・進むではURLだけ変わるため、表示と検索条件をURLへ戻して再検索する
    useLayoutEffect(() => {
        var _a, _b, _c;
        if (location.key !== initialLocationKeyRef.current)
            hasLeftInitialEntryRef.current = true;
        if (navigationType !== 'POP' || !hasLeftInitialEntryRef.current)
            return;
        const urlQuery = (query.get('q') || '').trim();
        const urlTagIDs = parseIdList(query.get('tag_id'));
        const urlTrainerIDs = parseIdList(query.get('trainer_id'));
        const openedAsFilter = urlTagIDs.length > 0 || urlTrainerIDs.length > 0;
        let nextTagIDs = urlTagIDs;
        let nextTrainerIDs = urlTrainerIDs;
        let nextKeyword = openedAsFilter ? '' : query.get('q') || '';
        if (!openedAsFilter && urlQuery && (partTags.length || trainers.length)) {
            const matched = matchQueryNames(urlQuery, partTags, trainers);
            if (matched.tagIDs.length || matched.trainerIDs.length) {
                nextTagIDs = matched.tagIDs;
                nextTrainerIDs = matched.trainerIDs;
                nextKeyword = matched.keyword;
            }
        }
        const categoryQuery = query.get('category');
        const nextCategory = categoryQuery === 'series' || categoryQuery === 'playlist' ? categoryQuery : 'lesson';
        const nextSort = query.get('sort_by') || SelectOption.Favorite;
        setStyleIDs(((_a = query.get('style_id')) === null || _a === void 0 ? void 0 : _a.split(',').map(Number)) || []);
        setDifficultyIDs(((_b = query.get('difficulty_id')) === null || _b === void 0 ? void 0 : _b.split(',').map(Number)) || []);
        setDurationIDs(((_c = query.get('duration_id')) === null || _c === void 0 ? void 0 : _c.split(',').map(Number)) || []);
        setTagIDs(nextTagIDs);
        setTrainerIDs(nextTrainerIDs);
        setContentCategory(nextCategory);
        setTab(nextCategory === 'lesson'
            ? SearchResultSectionTabs[query.get(SEARCH_SELECTED_TAB)] || SearchResultSectionTabs.lessons
            : resultSectionForCategory(nextCategory));
        setKeyword(nextKeyword);
        setSortBy(nextSort);
        setShowSortDialog(false);
        unresolvedQueryRef.current = openedAsFilter ? urlQuery : '';
        keywordEditedRef.current = false;
        setPageStatus(PageStatus.SearchingStart);
        setLessonsCount(undefined);
        setSeriesProgramsCount(undefined);
        setPlaylistsCount(undefined);
        setHistorySearchTick((tick) => tick + 1);
    }, [location.key, navigationType]);
    useEffect(() => {
        if (historySearchTick === 0)
            return;
        search();
    }, [historySearchTick]);
    const changeNextButtonDisplayable = (pageCount, searchResult) => {
        var _a, _b, _c;
        if ((_a = searchResult.lessons) === null || _a === void 0 ? void 0 : _a.length) {
            setLessonsNextButtonDisplayable(searchResult.perPage * pageCount < searchResult.lessonsCount);
        }
        if ((_b = searchResult.programs) === null || _b === void 0 ? void 0 : _b.length) {
            setSeriesProgramsNextButtonDisplayable(searchResult.perPage * pageCount < searchResult.programsCount);
        }
        if ((_c = searchResult.playlists) === null || _c === void 0 ? void 0 : _c.length) {
            setPlaylistsNextButtonDisplayable(searchResult.perPage * pageCount < searchResult.playlistsCount);
        }
    };
    const setNextButtonDisplayable = (nextButtonDisplayable) => {
        if (lessons)
            setLessonsNextButtonDisplayable(nextButtonDisplayable);
        if (seriesPrograms)
            setSeriesProgramsNextButtonDisplayable(nextButtonDisplayable);
        if (playlists)
            setPlaylistsNextButtonDisplayable(nextButtonDisplayable);
    };
    const setLessonsAndPlaylistsAndPrograms = (nextPage, searchResult) => {
        var _a, _b, _c;
        const nextLessons = (_a = searchResult.lessons) !== null && _a !== void 0 ? _a : [];
        const nextPrograms = (_b = searchResult.programs) !== null && _b !== void 0 ? _b : [];
        const nextPlaylists = (_c = searchResult.playlists) !== null && _c !== void 0 ? _c : [];
        if (nextPage) {
            setLessons([...(lessons !== null && lessons !== void 0 ? lessons : []), ...nextLessons]);
            setSeriesPrograms([...(seriesPrograms !== null && seriesPrograms !== void 0 ? seriesPrograms : []), ...nextPrograms]);
            setPlaylists([...(playlists !== null && playlists !== void 0 ? playlists : []), ...nextPlaylists]);
        }
        else {
            setLessons(nextLessons);
            setSeriesPrograms(nextPrograms);
            setPlaylists(nextPlaylists);
            setLessonsCount(searchResult.lessonsCount);
            setSeriesProgramsCount(searchResult.programsCount);
            setPlaylistsCount(searchResult.playlistsCount);
        }
    };
    const onClickResultLesson = (lesson) => {
        tracker.trackClickSearchResultLesson(lesson.id);
    };
    const onClickResultProgram = (program) => {
        tracker.trackClickSearchResultProgram(program.id);
    };
    const onClickResultPlaylist = (playlist) => {
        tracker.trackClickSearchResultProgram(playlist.id);
    };
    const onClickBack = () => {
        navigate(`${routes.SEARCH.compile()}?${SEARCH_SELECTED_TAB}=${SearchTabKeys.categorySearch}`);
    };
    const onClickSortDialog = () => {
        setShowSortDialog(true);
    };
    const submitSortDialog = () => {
        setShowSortDialog(false);
        const text = composeSearchText(keyword, tagIDs, trainerIDs);
        navigate(`${routes.SEARCH_RESULT.compile()}${pcSearchQuery(text)}`);
        search(false, text, undefined, keyword);
    };
    const cancelSortDialog = () => {
        setShowSortDialog(false);
        setSortBy(currentSort);
    };
    const pcSearchQuery = (queryText, next) => {
        var _a, _b, _c;
        const queryList = [];
        const styles = (_a = next === null || next === void 0 ? void 0 : next.styleIDs) !== null && _a !== void 0 ? _a : styleIDs;
        const difficulties = (_b = next === null || next === void 0 ? void 0 : next.difficultyIDs) !== null && _b !== void 0 ? _b : difficultyIDs;
        const selectedDurations = (_c = next === null || next === void 0 ? void 0 : next.durationIDs) !== null && _c !== void 0 ? _c : durationIDs;
        const selectedCategory = next ? next.category : contentCategory;
        if (queryText)
            queryList.push(`q=${encodeURIComponent(queryText)}`);
        if (sortBy)
            queryList.push(`sort_by=${sortBy}`);
        if (styles.length)
            queryList.push(`style_id=${styles}`);
        if (difficulties.length)
            queryList.push(`difficulty_id=${difficulties}`);
        if (selectedDurations.length)
            queryList.push(`duration_id=${selectedDurations}`);
        queryList.push(`category=${selectedCategory || 'lesson'}`);
        return queryList.length ? `?${queryList.join('&')}` : '';
    };
    const applySearchFilters = (next) => {
        setStyleIDs(next.styleIDs);
        setDifficultyIDs(next.difficultyIDs);
        setDurationIDs(next.durationIDs);
        setTagIDs(next.tagIDs);
        setTrainerIDs(next.trainerIDs);
        setContentCategory(next.category);
        setTab(resultSectionForCategory(next.category));
        const queryText = composeSearchText(keyword, next.tagIDs, next.trainerIDs);
        navigate(`${routes.SEARCH_RESULT.compile()}${pcSearchQuery(queryText, next)}`);
        search(false, keyword, next);
    };
    const containsSearchPhrase = (text, name) => new RegExp(`(^|\\s)${escapeRegExp(name)}(?=\\s|$)`).test(text.trim());
    const composeSearchText = (keywordValue, selectedTagIDs, selectedTrainerIDs) => {
        const keywordText = keywordValue.trim();
        const keywordTokens = keywordText.split(/\s+/).filter(Boolean);
        const names = [
            ...selectedTagIDs.map((id) => { var _a; return (_a = partTags.find((tag) => tag.id === id)) === null || _a === void 0 ? void 0 : _a.name; }),
            ...selectedTrainerIDs.map((id) => { var _a; return (_a = trainers.find((trainer) => trainer.id === id)) === null || _a === void 0 ? void 0 : _a.name; }),
        ].filter((name) => !!name && !containsSearchPhrase(keywordText, name));
        return [...keywordTokens, ...names].join(' ');
    };
    const resultCount = contentCategory === 'series' ? seriesProgramsCount : contentCategory === 'playlist' ? playlistsCount : lessonsCount;
    return (React.createElement("div", { className: css(styles.container, showSortDialog && styles.openDialog) },
        React.createElement("div", { className: css(styles.searchContainer) },
            React.createElement(SearchResultHeader, { key: location.key, config: config, keyword: keyword, lessonsCount: resultCount, sortLabel: displaySortLabel, filters: { styleIDs, durationIDs, difficultyIDs, tagIDs, trainerIDs, category: contentCategory }, styles: lessonStyles || [], durations: durations || [], difficulties: difficulties || [], partTags: partTags, trainers: trainers, onBack: onClickBack, onSubmitKeyword: (value) => {
                    keywordEditedRef.current = true;
                    setKeyword(value);
                    const text = composeSearchText(value, tagIDs, trainerIDs);
                    navigate(`${routes.SEARCH_RESULT.compile()}${pcSearchQuery(text)}`);
                    search(false, text, undefined, value);
                }, onClearKeyword: () => {
                    keywordEditedRef.current = true;
                    setKeyword('');
                    const text = composeSearchText('', tagIDs, trainerIDs);
                    navigate(`${routes.SEARCH_RESULT.compile()}${pcSearchQuery(text)}`);
                    search(false, text, undefined, '');
                }, onClickSort: onClickSortDialog, onApplyFilters: applySearchFilters })),
        React.createElement(SearchResults, { lessons: lessons, seriesPrograms: seriesPrograms, playlists: playlists, lessonsCount: lessonsCount, seriesProgramsCount: seriesProgramsCount, playlistsCount: playlistsCount, onClickLesson: onClickResultLesson, onClickProgram: onClickResultProgram, onClickPlaylist: onClickResultPlaylist, onClickBack: onClickBack, selectedTab: tab, hideCount: true, search: search, lessonsNextButtonDisplayable: lessonsNextButtonDisplayable, playlistsNextButtonDisplayable: playlistsNextButtonDisplayable, seriesProgramsNextButtonDisplayable: seriesProgramsNextButtonDisplayable, pageStatus: pageStatus, isLoadingNext: isLoadingNext, addFavoriteLesson: apiClient.addFavoriteLesson, unFavoriteLesson: apiClient.unFavoriteLesson, addFavoriteProgram: apiClient.addFavoriteProgram, unFavoriteProgram: apiClient.unFavoriteProgram }),
        showSortDialog && (React.createElement(SearchSortDialog, { currentValue: sortBy, setCurrentValue: setSortBy, onClickSubmit: submitSortDialog, onClickCancel: cancelSortDialog }))));
};
const styles = StyleSheet.create({
    container: {
        margin: '0 auto',
        maxWidth: 1184,
        width: '100%',
        paddingBottom: FIXED_FOOTER_HEIGHT,
        [MediaBreakPointUp.MD]: {
            marginBottom: 40,
            padding: 0,
        },
    },
    openDialog: {
        overflewY: 'hidden',
    },
    searchContainer: {
        position: 'sticky',
        top: Sizes.MobileHeaderHeight,
        zIndex: ZIndex.Header,
        backgroundColor: Colors.White,
        textAlign: 'center',
        [MediaBreakPointUp.MD]: {
            top: 0,
        },
    },
});
